import { useMemo } from 'react'
import { HardDrive, Copy } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { RegistryCrudTable, type RegField, type RegRow } from '@/components/RegistryCrudTable'
import { fetchMediaStats, CAT_STR as S, type MediaStatRow } from './catalogApi'

interface DedupHashRow extends RegRow {
  id: number
  phash: string
  media_id: string
  status: string
  created_at: string
}

/**
 * A21.16 — Media saqlash statistikasi. GET /admin/analytics/media (files/file_variants
 * sof-agregat) → fayl soni/tayyor/xato, egalar, xotira (bayt), variantlar + tur-kesim.
 */
export function MediaStorageStatsScreen() {
  const columns = useMemo<AdminColumn<MediaStatRow>[]>(
    () => [
      { key: 'metric', header: S.metric, type: 'text', minWidth: 240 },
      { key: 'value', header: S.mValue, type: 'number', width: 160, align: 'right' },
      {
        key: 'note',
        header: S.note,
        type: 'text',
        minWidth: 140,
        render: (r) => <span className="text-secondary">{r.note || '—'}</span>,
      },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.mediaStats} screenId="A21.16" />
      <DataTable<MediaStatRow>
        screenId="A21.16"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchMediaStats}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <HardDrive size={22} /> }}
      />
    </>
  )
}

/**
 * A21.17 — Dublikat media aniqlash. Endi JONLI: admin perceptual-hash (pHash)
 * indeksini o'zi boshqaradi — real CRUD `/admin/media/dedup-hashes`
 * (`media_dedup_hashes`, migration 000249). Soxta ma'lumot yo'q.
 */
export function DuplicateMediaScreen() {
  const columns = useMemo<AdminColumn<DedupHashRow>[]>(
    () => [
      { key: 'phash', header: S.dedupPhash, type: 'text', minWidth: 200 },
      { key: 'media_id', header: S.dedupMediaId, type: 'text', minWidth: 160 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 150,
        filterable: true,
        options: [
          { value: 'pending', label: S.pending, status: 'pending' },
          { value: 'duplicate', label: 'duplicate', status: 'rejected' },
          { value: 'unique', label: 'unique', status: 'approved' },
          { value: 'ignored', label: S.rejected, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'text', width: 150 },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      { name: 'phash', placeholder: S.dedupPhash, mono: true },
      { name: 'media_id', placeholder: S.dedupMediaId, mono: true },
      {
        name: 'status',
        placeholder: S.status,
        type: 'select',
        options: [
          { value: 'pending', label: 'pending' },
          { value: 'duplicate', label: 'duplicate' },
          { value: 'unique', label: 'unique' },
          { value: 'ignored', label: 'ignored' },
        ],
      },
    ],
    [],
  )
  return (
    <RegistryCrudTable<DedupHashRow>
      screenId="A21.17"
      title={S.duplicates}
      description={S.dedupDesc}
      addLabel={S.addDedup}
      path="/admin/media/dedup-hashes"
      listKey="hashes"
      searchKeys={['phash', 'media_id', 'status']}
      fields={fields}
      columns={columns}
      icon={<Copy size={22} />}
    />
  )
}
